/**
 * MultipleDropdown 多选下拉组件
 * 继承 DropdownProxy，专注于多选内容的渲染和交互
 * 使用 CheckboxGroup 作为下拉内容
 * 使用 useEmitter 进行事件发布订阅
 */

(function (window, $) {
  class MultipleDropdown extends window.DropDownProxy {
    constructor(options) {
      // 提取 DropdownProxy 需要的配置
      const dropdownOptions = {
        $input: options.$input,
        $reference: options.$reference,
        trigger: options.trigger || "click",
        minWidth: options.minWidth || "inherit",
        maxWidth: options.maxWidth || "auto",
        placement: options.placement || "bottom-start",
        placementFixed: options.placementFixed || false,
        animation: options.animation !== false,
        zIndex: options.zIndex || (window.useZIndex ? window.useZIndex("dialog").content : 10),
        offset: options.offset || { left: 0, top: 0, right: 0, bottom: 0 },
        selfOnly: options.selfOnly || false,
        hoverHideThreshold: options.hoverHideThreshold || 100,
        canShow: options.canShow || null,
        canHide:
          options.canHide ||
          (() => {
            if (this.$filter && this.$filter.is(":focus")) {
              return false;
            } else {
              return true;
            }
          }),
      };

      // 调用父类构造函数
      super(dropdownOptions);

      // MultipleDropdown 特有的配置
      this.multipleOptions = $.extend(
        {
          useFilter: false,
          data: [], // { value: NumberString; name: string; disabled?: boolean}[]
          value: [], // 选中的值数组
          disabled: false,
          labelSlot: null, // Function (item) => string | DOM | jQuery
          headerSlot: null, // Function () => string | DOM | jQuery - 自定义头部内容
          footerSlot: null, // Function () => string | DOM | jQuery - 自定义底部内容
          dropdownClass: "", // 自定义下拉框样式类
          onChange: null, // 值变化回调
          filterCallback: null, // 过滤数据 返回 data - Function (keyword, data) => newData
        },
        options,
      );

      // 初始化多选组件
      this.checkboxGroup = null;
      this.$dropdownContent = null;
      this.$header = null;
      this.$filter = null;
      this.$footer = null;

      this.initMultipleDropdown();
    }

    initMultipleDropdown() {
      // 创建下拉内容容器
      this.$dropdownContent = $("<div>")
        .addClass("multiple-dropdown-content")
        .addClass(this.multipleOptions.dropdownClass);

      // 将内容容器添加到父类的 $html 中
      this.$html.append(this.$dropdownContent);

      // // 阻止点击下拉内容时关闭下拉框
      // this.$dropdownContent.on("click", (e) => {
      //   e.stopPropagation();
      // });

      // 渲染头部
      this.renderHeader();

      // 创建搜索 filter
      this.renderFilter();

      // 创建中间内容容器
      const $body = $("<div>").addClass("multiple-dropdown-body");
      this.$dropdownContent.append($body);

      // 初始化 CheckboxGroup
      this.checkboxGroup = new window.CheckboxGroup({
        data: this.multipleOptions.data,
        value: [...this.multipleOptions.value], // 复制数组避免引用问题
        disabled: this.multipleOptions.disabled,
        container: $body,
        labelSlot: this.multipleOptions.labelSlot,
      });

      // 渲染底部
      this.renderFooter();

      // 监听 CheckboxGroup 的 change 事件
      this.checkboxGroup.emitter.on("change", (values) => {
        this.handleChange(values);
      });
    }

    renderHeader() {
      const { headerSlot } = this.multipleOptions;

      if (!headerSlot) return;

      this.$header = $("<div>").addClass("multiple-dropdown-header");

      if (typeof headerSlot === "function") {
        const content = headerSlot();
        if (typeof content === "string") {
          this.$header.html(content);
        } else if (content instanceof jQuery || (content && content.nodeType)) {
          this.$header.append(content);
        }
      } else if (typeof headerSlot === "string") {
        this.$header.html(headerSlot);
      } else if (headerSlot instanceof jQuery || (headerSlot && headerSlot.nodeType)) {
        this.$header.append(headerSlot);
      }

      this.$dropdownContent.append(this.$header);
    }

    renderFilter() {
      const { useFilter } = this.multipleOptions;
      if (!useFilter) return;

      const $box = $("<div>").addClass("multiple-dropdown-filter-box");
      const $icon = $("<i>").addClass("iconfont icon-search search");
      const $clear = $("<i>").addClass("iconfont icon-close-circle-filled clear");
      const $filter = (this.$filter = $('<input placeholder="请输入关键词进行搜索">'));

      const handleInput = async () => {
        const val = $filter.val();
        $clear.toggleClass("hover-show", !!val);

        if (typeof this.multipleOptions.filterCallback === "function") {
          const filteredData = await this.multipleOptions.filterCallback(
            val,
            this.multipleOptions.data,
          );
          this.checkboxGroup.updateData(filteredData);
        } else if (!val) {
          this.checkboxGroup.updateData(this.multipleOptions.data);
        } else {
          this.checkboxGroup.updateData(
            this.multipleOptions.data.filter(({ name }) => ~name.indexOf(val)),
          );
        }
      }

      $filter
        .on("focus", () => $box.addClass("focus"))
        .on("blur", () => $box.removeClass("focus"))
        .on("input", useDebounce(handleInput, 300));

      $clear.on("click", () => {
        $filter.val("").trigger("input");
      });

      this.emitter.on("toggleShow", (isShow) => {
        !isShow && $filter.val() && $clear.click();
      });

      this.$dropdownContent.append($box.append($icon, $filter, $clear));
    }

    renderFooter() {
      const { footerSlot } = this.multipleOptions;

      if (!footerSlot) return;

      this.$footer = $("<div>").addClass("multiple-dropdown-footer");

      if (typeof footerSlot === "function") {
        const content = footerSlot();
        if (typeof content === "string") {
          this.$footer.html(content);
        } else if (content instanceof jQuery || (content && content.nodeType)) {
          this.$footer.append(content);
        }
      } else if (typeof footerSlot === "string") {
        this.$footer.html(footerSlot);
      } else if (footerSlot instanceof jQuery || (footerSlot && footerSlot.nodeType)) {
        this.$footer.append(footerSlot);
      }

      this.$dropdownContent.append(this.$footer);
    }

    handleChange(values) {
      this.multipleOptions.value = [...values];

      // 触发 change 事件
      this.emitter.emit("change", values);

      // 调用回调函数
      if (typeof this.multipleOptions.onChange === "function") {
        this.multipleOptions.onChange(values);
      }
    }

    /**
     * 获取当前选中的值
     * @returns {Array} 选中的值数组
     */
    getValue() {
      return this.checkboxGroup ? this.checkboxGroup.getValue() : [];
    }

    /**
     * 设置选中的值
     * @param {Array} values - 要设置的值数组
     */
    setValue(values) {
      if (!Array.isArray(values)) {
        console.warn("MultipleDropdown [Warning] => setValue expects an array");
        return;
      }

      this.multipleOptions.value = [...values];

      if (this.checkboxGroup) {
        this.checkboxGroup.setValue(values);
      }
    }

    /**
     * 设置禁用状态
     * @param {Boolean} disabled - 是否禁用
     */
    setDisabled(disabled) {
      this.multipleOptions.disabled = disabled;

      if (this.checkboxGroup) {
        this.checkboxGroup.setDisabled(disabled);
      }

      // 禁用输入框
      if (disabled) {
        this.$input.addClass("is-disabled");
      } else {
        this.$input.removeClass("is-disabled");
      }
    }

    /**
     * 更新数据源
     * @param {Array} data - 新的数据数组
     */
    updateData(data) {
      if (!Array.isArray(data)) {
        console.warn("MultipleDropdown [Warning] => updateData expects an array");
        return;
      }

      this.multipleOptions.data = data;

      if (this.checkboxGroup) {
        this.checkboxGroup.updateData(data);
      }
    }

    /**
     * 销毁组件
     */
    destroy() {
      // 移除事件监听
      if (this.checkboxGroup && this.checkboxGroup.emitter) {
        this.checkboxGroup.emitter.off("change");
      }

      // 移除内容容器
      if (this.$dropdownContent) {
        this.$dropdownContent.remove();
      }

      // 清空引用
      this.checkboxGroup = null;
      this.$dropdownContent = null;

      // 注意：父类的 $html 会在元素移除时自动清理，不需要手动调用
    }
  }

  // 导出到全局
  window.MultipleDropdown = MultipleDropdown;
})(window, jQuery);
